// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useContext } from "react";
import { css } from "styled-components";

// XNG-Change: Add those options for the widget usability
const enum fields {
    showImage = "showImage",
    showLinks = "showLinks",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showImage]: boolean;
        [fields.showLinks]: boolean;
    };
}

type Props = OwnProps & HasHistory;

export interface BrandDetailsImageStyles {
    container?: GridContainerProps;
    image?: LazyImageProps;
    imageMissingText?: TypographyPresentationProps;
    linksItem?: GridItemProps;
    linksContainer?: GridContainerProps;
    productLinkItem?: GridItemProps;
    externalLinkItem?: GridItemProps;
    // XNG-Changes
    imageItem?: InjectableCss;
    productLink?: ButtonPresentationProps;
    externalLink?: ButtonPresentationProps;
}

export const imageStyles: BrandDetailsImageStyles = {
    container: {
        gap: 5,
        css: css`
            margin: 15px;
        `,
    },
    imageItem: {
        // width: 12,
    },
    linksItem: {
        width: 12,
    },
    linksContainer: {
        gap: 20,
        css: css`
            padding-top: 15px;
        `,
    },
    image: {
        css: css`
            img {
                height: 100%;
            }
        `,
    },
    imageMissingText: {
        variant: "h3",
        css: css`
            width: 100%;
            overflow-wrap: break-word;
            word-wrap: break-word;
        `,
    },
    productLinkItem: {
        width: 12,
        css: css`
            justify-content: center;
            text-align: center;
        `,
    },
    externalLinkItem: {
        width: 12,
        css: css`
            justify-content: center;
            text-align: center;
        `,
    },
};

const styles = imageStyles;

const BrandDetailsImage: FC<Props> = ({ fields, history }) => {
    const { value: brand } = useContext(BrandStateContext);
    const { showImage, showLinks } = fields;
    if (!brand) {
        return null;
    }

    // XNG-Change: Change structure
    // No need to display brand name if image doesn't exist
    return (
        <StyledWrapper data-test-selector="brandImage" {...styles.container}>
            {showImage && (
                <StyledWrapper {...styles.imageItem}>
                    {brand.featuredImagePath && (
                        <LazyImage
                            data-test-selector="brandFeaturedImage"
                            altText={brand.featuredImageAltText}
                            src={brand.featuredImagePath}
                            {...styles.image}
                        />
                    )}
                </StyledWrapper>
            )}
            {showLinks && (
                <StyledWrapper {...styles.linksItem}>
                    <Button
                        data-test-selector="brandToProductPageLink"
                        onClick={() => history.push(brand.productListPagePath)}
                        {...styles.productLink}
                        aria-label={translate("Shop All Products")}
                    >
                        {translate("Shop All Products")}
                    </Button>

                    {brand.externalUrl && (
                        <Button
                            data-test-selector="brandExternalUrlLink"
                            onClick={() => history.push(brand.externalUrl)}
                            {...styles.externalLink}
                            aria-label="Visit Website"
                        >
                            {translate("Visit {0} Website").replace("{0}", brand.name)}
                        </Button>
                    )}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

interface BrandDetailsLinkProps {
    path?: string;
    gridItemStyles?: GridItemProps;
    linkStyles?: LinkPresentationProps;
}

const BrandLink: FC<BrandDetailsLinkProps> = ({ path, gridItemStyles, linkStyles, children }) => {
    if (!path) {
        return null;
    }
    return (
        <GridItem {...gridItemStyles}>
            <Link aria-label="brand link" data-test-selector="brandToProductPageLink" href={path} {...linkStyles}>
                {children}
            </Link>
        </GridItem>
    );
};

const widgetModule: WidgetModule = {
    component: withHistory(BrandDetailsImage),
    definition: {
        group: "Brand Details",
        icon: "image",
        displayName: "Brand Image",
        allowedContexts: ["BrandDetailsPage"],
        // XNG-Change: add custom fields
        fieldDefinitions: [
            {
                name: fields.showImage,
                displayName: "Show Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showLinks,
                displayName: "Show Links",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
